import { db, auth, isFirebaseConfigured } from "../utils/firebase";
import { collection, doc, setDoc, addDoc, getDocs, query, where, orderBy, onSnapshot, updateDoc, deleteDoc } from "firebase/firestore";
import { StorageService } from "./storage/StorageService";

export interface AppNotification {
  id: string;
  userId?: string;
  userEmail?: string;
  userCpf?: string;
  senderId?: string;
  senderUserId?: string;
  eventId?: string;
  idempotencyKey?: string;
  title: string;
  message: string;
  type: "dues_pending" | "booking_confirmed" | "booking_cancelled" | "lesson_created" | "tournament_request" | "system" | string;
  linkUrl?: string;
  metadata?: Record<string, any>;
  read: boolean;
  createdAt: string;
}

const LOCAL_STORAGE_KEY = "app_user_notifications_v1";
const localSubscribers = new Set<(notifications: AppNotification[]) => void>();

/**
 * Normaliza qualquer formato de data (Timestamp do Firestore, objeto seconds/nanoseconds, Date, string ISO, unix millis)
 * para um valor numérico em milissegundos determinístico para ordenação.
 */
export function normalizeNotificationTimestamp(createdAt: any): number {
  if (!createdAt) return 0;
  if (typeof createdAt === "number") return createdAt;
  if (createdAt instanceof Date) return isNaN(createdAt.getTime()) ? 0 : createdAt.getTime();
  if (typeof createdAt.toMillis === "function") return createdAt.toMillis();
  if (typeof createdAt.toDate === "function") {
    const d = createdAt.toDate();
    return d instanceof Date && !isNaN(d.getTime()) ? d.getTime() : 0;
  }
  if (typeof createdAt.seconds === "number") {
    return createdAt.seconds * 1000 + Math.floor((createdAt.nanoseconds || 0) / 1000000);
  }
  if (typeof createdAt._seconds === "number") {
    return createdAt._seconds * 1000 + Math.floor((createdAt._nanoseconds || 0) / 1000000);
  }
  if (typeof createdAt === "string") {
    const parsed = new Date(createdAt).getTime();
    if (!isNaN(parsed)) return parsed;
    const ddmmyyyy = createdAt.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})(?:\s+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?/);
    if (ddmmyyyy) {
      const day = parseInt(ddmmyyyy[1], 10);
      const month = parseInt(ddmmyyyy[2], 10) - 1;
      const year = parseInt(ddmmyyyy[3], 10);
      const hour = ddmmyyyy[4] ? parseInt(ddmmyyyy[4], 10) : 0;
      const min = ddmmyyyy[5] ? parseInt(ddmmyyyy[5], 10) : 0;
      const sec = ddmmyyyy[6] ? parseInt(ddmmyyyy[6], 10) : 0;
      const d = new Date(year, month, day, hour, min, sec);
      if (!isNaN(d.getTime())) return d.getTime();
    }
  }
  return 0;
}

/**
 * Converte qualquer representação de data para string ISO 8601 válida
 */
export function normalizeNotificationIsoDate(createdAt: any): string {
  const ts = normalizeNotificationTimestamp(createdAt);
  if (ts > 0) {
    return new Date(ts).toISOString();
  }
  return typeof createdAt === "string" ? createdAt : new Date().toISOString();
}

/**
 * Cria e salva uma notificação para um usuário no Firestore e localmente
 */
export async function createUserNotification(params: {
  id?: string;
  userId?: string;
  userEmail?: string;
  userCpf?: string;
  senderId?: string;
  senderUserId?: string;
  eventId?: string;
  idempotencyKey?: string;
  title: string;
  message: string;
  type: AppNotification["type"];
  linkUrl?: string;
  metadata?: Record<string, any>;
  adminDb?: any;
}): Promise<AppNotification> {
  const notifId =
    params.id ||
    params.eventId ||
    params.idempotencyKey ||
    `notif_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

  const notification: AppNotification = {
    id: notifId,
    userId: params.userId || "",
    userEmail: (params.userEmail || "").trim().toLowerCase(),
    userCpf: (params.userCpf || "").replace(/\D/g, ""),
    senderId: params.senderId || params.senderUserId || "",
    senderUserId: params.senderUserId || params.senderId || "",
    eventId: params.eventId || params.idempotencyKey || "",
    idempotencyKey: params.idempotencyKey || params.eventId || "",
    title: params.title,
    message: params.message,
    type: params.type,
    linkUrl: params.linkUrl || "",
    metadata: params.metadata || {},
    read: false,
    createdAt: new Date().toISOString(),
  };

  // 1. Salva no StorageService e notifica subscribers locais
  try {
    const list: AppNotification[] = StorageService.get<AppNotification[]>(LOCAL_STORAGE_KEY, []) || [];
    const updated = [notification, ...list.filter((n) => n.id !== notifId)];
    StorageService.set(LOCAL_STORAGE_KEY, updated);
    localSubscribers.forEach((cb) => {
      try { cb(updated); } catch {}
    });
    const target = typeof window !== "undefined" ? window : (typeof globalThis !== "undefined" ? globalThis : null);
    if (target && typeof (target as any).dispatchEvent === "function" && typeof CustomEvent !== "undefined") {
      try {
        (target as any).dispatchEvent(new CustomEvent("app_notifications_updated", { detail: updated }));
      } catch {}
    }
  } catch (err) {
    console.error("Erro ao salvar notificação local:", err);
  }

  // 2. Salva no Firestore (Admin DB no backend ou Client SDK no frontend)
  if (params.adminDb) {
    try {
      await params.adminDb.collection("notifications").doc(notifId).set(notification, { merge: true });
      if (params.userId && params.userId !== "guest") {
        await params.adminDb.collection("users").doc(params.userId).collection("notifications").doc(notifId).set(notification, { merge: true });
      }
    } catch (adminErr) {
      console.warn("Aviso ao salvar notificação via adminDb:", adminErr);
    }
  } else if (isFirebaseConfigured() && db) {
    try {
      // Salva na coleção global "notifications"
      const docRef = doc(db, "notifications", notifId);
      await setDoc(docRef, notification, { merge: true });

      // Se tiver userId explícito, salva também na subcoleção do usuário
      if (params.userId && params.userId !== "guest") {
        const userNotifRef = doc(db, `users/${params.userId}/notifications`, notifId);
        await setDoc(userNotifRef, notification, { merge: true });
      }
    } catch (fsErr) {
      // Firestore warning
    }
  }

  return notification;
}

/**
 * Carrega notificações do usuário atual a partir do cache local com normalização e ordenação rigorosa
 */
export function getLocalUserNotifications(userEmail?: string, userCpf?: string, userId?: string): AppNotification[] {
  try {
    const list: AppNotification[] = StorageService.get<AppNotification[]>(LOCAL_STORAGE_KEY, []) || [];
    const cleanEmail = (userEmail || "").trim().toLowerCase();
    const cleanCpf = (userCpf || "").replace(/\D/g, "");
    const cleanUid = (userId || "").trim();

    // ISOLAMENTO RIGOROSO: Se não houver nenhum identificador de usuário, NUNCA retorna notificações de outros!
    if (!cleanEmail && !cleanCpf && !cleanUid) {
      return [];
    }

    const filtered = list.filter((n) => {
      const notifUid = String(n.userId || "").trim();
      const notifEmail = String(n.userEmail || "").trim().toLowerCase();
      const notifCpf = String(n.userCpf || "").replace(/\D/g, "");

      if (cleanUid && notifUid && notifUid === cleanUid) return true;
      if (cleanEmail && notifEmail && notifEmail === cleanEmail) return true;
      if (cleanCpf && notifCpf && notifCpf === cleanCpf) return true;
      return false;
    });

    return filtered.sort((a, b) => normalizeNotificationTimestamp(b.createdAt) - normalizeNotificationTimestamp(a.createdAt));
  } catch (e) {
    return [];
  }
}

/**
 * Escuta notificações do usuário em tempo real cobrindo todas as origens possíveis:
 * UID, e-mail, CPF, subcoleção do usuário e cache local.
 */
export function subscribeUserNotifications(
  params: { userId?: string; userEmail?: string; userCpf?: string },
  callback: (notifications: AppNotification[]) => void
): () => void {
  const cleanEmail = (params.userEmail || "").trim().toLowerCase();
  const cleanCpf = (params.userCpf || "").replace(/\D/g, "");
  const cleanUid = (params.userId || "").trim();

  // ISOLAMENTO: Bloqueia vazamento se não houver identificador
  if (!cleanEmail && !cleanCpf && !cleanUid) {
    callback([]);
    return () => {};
  }

  // 1. Carrega estado inicial do cache local
  const initialLocal = getLocalUserNotifications(cleanEmail, cleanCpf, cleanUid);
  const consolidatedMap = new Map<string, AppNotification>();
  initialLocal.forEach((n) => {
    consolidatedMap.set(n.id, {
      ...n,
      createdAt: normalizeNotificationIsoDate(n.createdAt),
    });
  });

  const notifySubscribers = () => {
    const all = Array.from(consolidatedMap.values());
    all.sort((a, b) => normalizeNotificationTimestamp(b.createdAt) - normalizeNotificationTimestamp(a.createdAt));

    // Atualiza o storage preservando notificações consolidadas
    try {
      const existingInStorage = StorageService.get<AppNotification[]>(LOCAL_STORAGE_KEY, []) || [];
      const mergedStorageMap = new Map<string, AppNotification>();
      existingInStorage.forEach((n) => mergedStorageMap.set(n.id, n));
      all.forEach((n) => mergedStorageMap.set(n.id, n));
      const consolidatedStorageList = Array.from(mergedStorageMap.values());
      consolidatedStorageList.sort((a, b) => normalizeNotificationTimestamp(b.createdAt) - normalizeNotificationTimestamp(a.createdAt));
      StorageService.set(LOCAL_STORAGE_KEY, consolidatedStorageList);
    } catch {}

    callback(all);
  };

  const unsubs: Array<() => void> = [];

  // Notifica o estado inicial síncrono imediatamente
  notifySubscribers();

  // 1. Registra no conjunto de subscribers em memória
  const memoryListener = (fullList: AppNotification[]) => {
    const matching = fullList.filter((n) => {
      if (cleanUid && n.userId === cleanUid) return true;
      if (cleanEmail && n.userEmail === cleanEmail) return true;
      if (cleanCpf && n.userCpf === cleanCpf) return true;
      return false;
    });
    matching.forEach((n) => consolidatedMap.set(n.id, n));
    notifySubscribers();
  };
  localSubscribers.add(memoryListener);
  unsubs.push(() => localSubscribers.delete(memoryListener));

  if (!isFirebaseConfigured() || !db) {
    return () => {
      unsubs.forEach((u) => {
        try { u(); } catch {}
      });
    };
  }

  const handleSnapshot = (snap: any) => {
    if (!snap || typeof snap.forEach !== "function") return;
    snap.forEach((d: any) => {
      const data = d.data ? d.data() : d;
      const notifId = d.id || data.id;
      if (!notifId) return;
      const normDate = normalizeNotificationIsoDate(data.createdAt || data.timestamp || data.date);
      consolidatedMap.set(notifId, {
        id: notifId,
        ...data,
        createdAt: normDate,
      } as AppNotification);
    });
    notifySubscribers();
  };

  try {
    // 1. Escuta notificações onde userId == cleanUid na coleção global
    if (cleanUid) {
      const qUid = query(collection(db, "notifications"), where("userId", "==", cleanUid));
      unsubs.push(onSnapshot(qUid, handleSnapshot, (err) => console.warn("[notif listener uid warn]:", err)));
    }

    // 2. Escuta notificações onde userEmail == cleanEmail na coleção global
    if (cleanEmail) {
      const qEmail = query(collection(db, "notifications"), where("userEmail", "==", cleanEmail));
      unsubs.push(onSnapshot(qEmail, handleSnapshot, (err) => console.warn("[notif listener email warn]:", err)));
    }

    // 3. Escuta notificações onde userCpf == cleanCpf na coleção global
    if (cleanCpf) {
      const qCpf = query(collection(db, "notifications"), where("userCpf", "==", cleanCpf));
      unsubs.push(onSnapshot(qCpf, handleSnapshot, (err) => console.warn("[notif listener cpf warn]:", err)));
    }

    // 4. Escuta na subcoleção do usuário /users/{cleanUid}/notifications
    if (cleanUid && cleanUid !== "guest") {
      const qSub = collection(db, "users", cleanUid, "notifications");
      unsubs.push(onSnapshot(qSub, handleSnapshot, (err) => console.warn("[notif listener subcollection warn]:", err)));
    }

    // 5. Escuta atualizações locais da janela / runtime
    const localHandler = (e: any) => {
      if (e?.detail && Array.isArray(e.detail)) {
        const matching = (e.detail as AppNotification[]).filter((n) => {
          if (cleanUid && n.userId === cleanUid) return true;
          if (cleanEmail && n.userEmail === cleanEmail) return true;
          if (cleanCpf && n.userCpf === cleanCpf) return true;
          return false;
        });
        matching.forEach((n) => consolidatedMap.set(n.id, n));
        notifySubscribers();
      }
    };
    const target = typeof window !== "undefined" ? window : (typeof globalThis !== "undefined" ? globalThis : null);
    if (target && typeof (target as any).addEventListener === "function") {
      (target as any).addEventListener("app_notifications_updated", localHandler);
      unsubs.push(() => {
        try {
          (target as any).removeEventListener("app_notifications_updated", localHandler);
        } catch {}
      });
    }

    return () => {
      unsubs.forEach((u) => {
        try { u(); } catch {}
      });
    };
  } catch (err) {
    console.warn("[subscribeUserNotifications error]:", err);
    return () => {
      unsubs.forEach((u) => {
        try { u(); } catch {}
      });
    };
  }
}

/**
 * Marca uma notificação específica como lida
 */
export async function markNotificationAsRead(notifId: string, userId?: string): Promise<void> {
  try {
    const list: AppNotification[] = StorageService.get<AppNotification[]>(LOCAL_STORAGE_KEY, []) || [];
    const updated = list.map((n) => (n.id === notifId ? { ...n, read: true } : n));
    StorageService.set(LOCAL_STORAGE_KEY, updated);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("app_notifications_updated", { detail: updated }));
    }
  } catch (err) {}

  if (isFirebaseConfigured() && db) {
    try {
      const docRef = doc(db, "notifications", notifId);
      await updateDoc(docRef, { read: true });
      if (userId && userId !== "guest") {
        const userNotifRef = doc(db, `users/${userId}/notifications`, notifId);
        await updateDoc(userNotifRef, { read: true });
      }
    } catch (err) {}
  }
}

/**
 * Marca todas as notificações da lista do usuário como lidas
 */
export async function markAllNotificationsAsRead(notifications: AppNotification[], userId?: string): Promise<void> {
  const unread = notifications.filter((n) => !n.read);
  for (const n of unread) {
    await markNotificationAsRead(n.id, userId || n.userId);
  }
}
